import React, { useEffect, useMemo, useState, useRef } from "react";
import MDSpinner from "react-md-spinner";
import GoogleMapReact from "google-map-react";
import axios from "axios";
import html2canvas from "html2canvas";
import { FaInfoCircle } from "react-icons/fa";
import myConfig from "../../configs/config";
import { useParams } from "react-router-dom";
import SidebarToolInfoModal from "../../components/SidebarToolInfoModal";
import MapViewportToolbar from "../../components/MapViewportToolbar";
import { applyGradualMapZoom, withGradualZoomOptions } from "../../utils/gradualMapZoom";
import {
  MAP_VIEWPORT_SCOPES,
  applyViewportToGoogleMap,
  readLocalMapViewport,
  readViewportFromGoogleMap,
  resolveMapViewport,
  writeLocalMapViewport,
} from "../../utils/mapViewport";
import {
  RUMO_PISTA_HELP_TITLE,
  RumoPistaHelpContent,
} from "./rumoPistaHelpContent";

import "./RumoPistaAB.css";

function RumoPistaAB() {
  const { farm_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [farmCenter, setFarmCenter] = useState(null);
  const [savedMapViewport, setSavedMapViewport] = useState(null);
  const [bearingData, setBearingData] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [captureMessage, setCaptureMessage] = useState("");

  const mapApiRef = useRef(null);
  const mapWrapperRef = useRef(null);
  const markerARef = useRef(null);
  const markerBRef = useRef(null);
  const lineRef = useRef(null);
  const clickCountRef = useRef(0);

  useEffect(() => {
    document.title = "Agriconnected - Rumo de Pista A-B";
    getCenterOfCurrentFarm();
  }, []);

  async function getCenterOfCurrentFarm() {
    const url = `${myConfig.API_URL}/farm/${farm_id}/`;
    await axios({
      baseURL: url,
      method: "get",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("client-token")}`,
      },
    }).then((res) => {
      setFarmCenter(res.data.farm_center);
      setSavedMapViewport(res.data.map_viewport || null);
      setLoading(false);
    });
  }

  const initialMapViewport = useMemo(() => {
    if (!farmCenter) return null;
    return resolveMapViewport({
      apiViewport: savedMapViewport,
      localViewport: readLocalMapViewport(
        MAP_VIEWPORT_SCOPES.RUMO_PISTA,
        farm_id
      ),
      fallbackCenter: farmCenter,
      fallbackZoom: 18,
    });
  }, [farmCenter, savedMapViewport, farm_id]);

  function handleSaveMapViewport() {
    if (!mapApiRef.current) return;
    const viewport = readViewportFromGoogleMap(mapApiRef.current);
    if (!viewport) return;
    writeLocalMapViewport(MAP_VIEWPORT_SCOPES.RUMO_PISTA, farm_id, viewport);
    setSavedMapViewport(viewport);
    setCaptureMessage("Posição e zoom do mapa salvos.");
  }

  function calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function calculateBearing(lat1, lng1, lat2, lng2) {
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const lat1Rad = (lat1 * Math.PI) / 180;
    const lat2Rad = (lat2 * Math.PI) / 180;

    const x = Math.sin(dLng) * Math.cos(lat2Rad);
    const y =
      Math.cos(lat1Rad) * Math.sin(lat2Rad) -
      Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);

    let bearing = (Math.atan2(x, y) * 180) / Math.PI;
    bearing = (bearing + 360) % 360;
    return bearing;
  }

  function formatDistance(meters) {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(2)} km`;
    }
    return `${meters.toFixed(2)} m`;
  }

  function updateBearingData(pointA, pointB) {
    const distance = calculateDistance(
      pointA.lat,
      pointA.lng,
      pointB.lat,
      pointB.lng,
    );
    const bearing = calculateBearing(
      pointA.lat,
      pointA.lng,
      pointB.lat,
      pointB.lng,
    );
    setBearingData({
      pointA: { lat: pointA.lat, lng: pointA.lng },
      pointB: { lat: pointB.lat, lng: pointB.lng },
      distance,
      bearing,
    });
  }

  function updateLine(pointA, pointB) {
    if (lineRef.current) {
      lineRef.current.setPath([pointA, pointB]);
    }
  }

  function clearBearingTool() {
    if (markerARef.current) {
      markerARef.current.setMap(null);
      markerARef.current = null;
    }
    if (markerBRef.current) {
      markerBRef.current.setMap(null);
      markerBRef.current = null;
    }
    if (lineRef.current) {
      lineRef.current.setMap(null);
      lineRef.current = null;
    }
    clickCountRef.current = 0;
    setBearingData(null);
  }

  function setupBearingTool(google) {
    mapApiRef.current = google.map;
    applyGradualMapZoom(google.map);
    if (initialMapViewport) {
      applyViewportToGoogleMap(google.map, initialMapViewport);
    }

    google.maps.event.addListener(google.map, "click", function (event) {
      if (event.domEvent) {
        event.domEvent.stopPropagation();
        event.domEvent.preventDefault();
      }

      const clickedLatLng = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      if (clickCountRef.current === 0) {
        clearBearingTool();

        markerARef.current = new google.maps.Marker({
          position: clickedLatLng,
          map: google.map,
          label: { text: "A", color: "white", fontWeight: "bold" },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 15,
            fillColor: "#4CAF50",
            fillOpacity: 1,
            strokeColor: "#2E7D32",
            strokeWeight: 2,
          },
          draggable: true,
        });

        google.maps.event.addListener(markerARef.current, "drag", function () {
          if (markerBRef.current) {
            const posA = markerARef.current.getPosition();
            const posB = markerBRef.current.getPosition();
            const pointA = { lat: posA.lat(), lng: posA.lng() };
            const pointB = { lat: posB.lat(), lng: posB.lng() };
            updateLine(pointA, pointB);
            updateBearingData(pointA, pointB);
          }
        });

        clickCountRef.current = 1;
      } else if (clickCountRef.current === 1) {
        markerBRef.current = new google.maps.Marker({
          position: clickedLatLng,
          map: google.map,
          label: { text: "B", color: "white", fontWeight: "bold" },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 15,
            fillColor: "#F44336",
            fillOpacity: 1,
            strokeColor: "#C62828",
            strokeWeight: 2,
          },
          draggable: true,
        });

        const posA = markerARef.current.getPosition();
        const pointA = { lat: posA.lat(), lng: posA.lng() };
        const pointB = clickedLatLng;

        lineRef.current = new google.maps.Polyline({
          path: [pointA, pointB],
          geodesic: true,
          strokeColor: "#2196F3",
          strokeOpacity: 1.0,
          strokeWeight: 3,
          map: google.map,
        });

        updateBearingData(pointA, pointB);

        google.maps.event.addListener(markerBRef.current, "drag", function () {
          const posA = markerARef.current.getPosition();
          const posB = markerBRef.current.getPosition();
          const pointA = { lat: posA.lat(), lng: posA.lng() };
          const pointB = { lat: posB.lat(), lng: posB.lng() };
          updateLine(pointA, pointB);
          updateBearingData(pointA, pointB);
        });

        clickCountRef.current = 2;
      } else {
        clearBearingTool();
        clickCountRef.current = 0;
      }
    });
  }

  function formatPilotExport(data) {
    return [
      "Rumo de Pista A-B — AgriConnected",
      `Ponto A: ${data.pointA.lat.toFixed(6)}, ${data.pointA.lng.toFixed(6)}`,
      `Ponto B: ${data.pointB.lat.toFixed(6)}, ${data.pointB.lng.toFixed(6)}`,
      `Rumo A→B: ${data.bearing.toFixed(2)}°`,
      `Distância: ${formatDistance(data.distance)}`,
    ].join("\n");
  }

  async function copyPilotData() {
    if (!bearingData) return;
    try {
      await navigator.clipboard.writeText(formatPilotExport(bearingData));
      setCaptureMessage("Coordenadas copiadas para a área de transferência.");
    } catch (error) {
      setCaptureMessage("Não foi possível copiar automaticamente.");
    }
  }

  async function captureMapImage() {
    if (!mapWrapperRef.current) return;
    setCaptureMessage("Gerando captura...");
    try {
      const canvas = await html2canvas(mapWrapperRef.current, {
        useCORS: true,
        allowTaint: true,
        logging: false,
      });
      const link = document.createElement("a");
      link.download = `rumo-pista-ab-${farm_id}-${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      setCaptureMessage("Captura salva. Se o mapa sair em branco, use Imprimir.");
    } catch (error) {
      setCaptureMessage("Falha na captura. Use Imprimir ou copie as coordenadas.");
    }
  }

  function printSummary() {
    if (!bearingData) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <html><head><title>Rumo A-B</title></head><body style="font-family:sans-serif;padding:24px">
      <h2>Rumo de Pista A-B</h2>
      <pre>${formatPilotExport(bearingData)}</pre>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }

  function getMapOptions(maps) {
    return withGradualZoomOptions({
      streetViewControl: true,
      scaleControl: true,
      fullscreenControl: true,
      gestureHandling: "greedy",
      disableDoubleClickZoom: false,
      minZoom: 2,
      maxZoom: 22,
      mapTypeId: maps.MapTypeId.SATELLITE,
      clickableIcons: false,
    });
  }

  return (
    <div className="rumo-page">
      <div className="rumo-page-header">
        <div className="rumo-page-header-row">
          <h3>
            <span role="img" aria-label="bússola">
              🧭
            </span>{" "}
            Rumo de Pista A-B
          </h3>
          <button
            type="button"
            className="rumo-info-btn"
            title="O que é esta ferramenta?"
            onClick={() => setShowHelp(true)}
          >
            <FaInfoCircle />
          </button>
        </div>
        <p className="rumo-page-subtitle">
          Marque A e B no satélite para definir a direção do trabalho e
          orientar o piloto automático da máquina. Arraste os pontos para
          ajustar; use zoom máximo para precisão.
        </p>
      </div>

      <div className="rumo-map-container">
        {loading ? (
          <div className="rumo-spinner">
            <MDSpinner />
          </div>
        ) : (
          <div className="rumo-map-wrapper" ref={mapWrapperRef}>
            <GoogleMapReact
              bootstrapURLKeys={{
                key: myConfig.GOOGLE_API_KEY,
              }}
              options={getMapOptions}
              defaultCenter={
                initialMapViewport
                  ? {
                      lat: initialMapViewport.lat,
                      lng: initialMapViewport.lng,
                    }
                  : { lat: 0, lng: 0 }
              }
              defaultZoom={initialMapViewport ? initialMapViewport.zoom : 18}
              yesIWantToUseGoogleMapApiInternals={true}
              onGoogleApiLoaded={setupBearingTool}
            />
            <MapViewportToolbar
              onSave={handleSaveMapViewport}
              disabled={loading || !initialMapViewport}
              label="Salvar posição do mapa"
            />

            {/* Instrução inicial */}
            {!bearingData && (
              <div className="bearing-instructions">
                {clickCountRef.current === 0
                  ? "Clique no mapa para definir o Ponto A (Origem)"
                  : "Clique no mapa para definir o Ponto B (Destino)"}
              </div>
            )}

            {/* Card de resultado */}
            {bearingData && (
              <div className="bearing-card">
                <div className="bearing-card-header">
                  <h4>
                    <span role="img" aria-label="pin">
                      📍
                    </span>{" "}
                    Calculadora de Rumo
                  </h4>
                  <button
                    type="button"
                    className="bearing-card-close"
                    onClick={clearBearingTool}
                  >
                    ×
                  </button>
                </div>
                <div className="bearing-card-body">
                  <div className="bearing-point">
                    <span className="point-label point-a">A</span>
                    <div className="point-coords">
                      <strong>Origem</strong>
                      <span>Lat: {bearingData.pointA.lat.toFixed(6)}</span>
                      <span>Lng: {bearingData.pointA.lng.toFixed(6)}</span>
                    </div>
                  </div>
                  <div className="bearing-point">
                    <span className="point-label point-b">B</span>
                    <div className="point-coords">
                      <strong>Destino</strong>
                      <span>Lat: {bearingData.pointB.lat.toFixed(6)}</span>
                      <span>Lng: {bearingData.pointB.lng.toFixed(6)}</span>
                    </div>
                  </div>
                  <div className="bearing-divider"></div>
                  <div className="bearing-results">
                    <div className="bearing-result-item">
                      <span
                        className="result-icon"
                        role="img"
                        aria-label="régua"
                      >
                        📏
                      </span>
                      <div className="result-data">
                        <strong>Distância</strong>
                        <span className="result-value">
                          {formatDistance(bearingData.distance)}
                        </span>
                      </div>
                    </div>
                    <div className="bearing-result-item">
                      <span
                        className="result-icon"
                        role="img"
                        aria-label="bússola"
                      >
                        🧭
                      </span>
                      <div className="result-data">
                        <strong>Rumo (Bearing)</strong>
                        <span className="result-value">
                          {bearingData.bearing.toFixed(2)}°
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="bearing-actions">
                    <button
                      type="button"
                      className="bearing-action-btn"
                      onClick={copyPilotData}
                    >
                      Copiar coordenadas
                    </button>
                    <button
                      type="button"
                      className="bearing-action-btn"
                      onClick={captureMapImage}
                    >
                      Salvar captura
                    </button>
                    <button
                      type="button"
                      className="bearing-action-btn"
                      onClick={printSummary}
                    >
                      Imprimir
                    </button>
                  </div>
                  {captureMessage && (
                    <p className="bearing-capture-msg">{captureMessage}</p>
                  )}
                  <div className="bearing-hint">
                    <span role="img" aria-label="dica">
                      💡
                    </span>{" "}
                    Arraste os marcadores A ou B para atualizar em tempo real
                  </div>
                  <button
                    type="button"
                    className="bearing-clear-btn"
                    onClick={clearBearingTool}
                  >
                    Limpar e recomeçar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {showHelp && (
        <SidebarToolInfoModal
          title={RUMO_PISTA_HELP_TITLE}
          onClose={() => setShowHelp(false)}
        >
          <RumoPistaHelpContent />
        </SidebarToolInfoModal>
      )}
    </div>
  );
}

export default RumoPistaAB;
